import { useEffect, useState } from "react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { ToolShell, TextInput, NumInput, SelectInput, ErrorBox } from "@/components/energy/tool";
import { api, formatApiError } from "@/lib/solarApi";

const TYPE_STYLES = {
  bnm_office: { color: "#F59E0B", label: "BNM Office" },
  bnm_project: { color: "#00E676", label: "BNM Project" },
  solar_installation: { color: "#34D399", label: "Solar Installation" },
  cfg_deployment: { color: "#7CF5B6", label: "Carbon-Free Generator" },
  community_request: { color: "#38BDF8", label: "Community Requesting Energy" },
  school_request: { color: "#818CF8", label: "School Requiring Energy" },
  clinic_request: { color: "#F472B6", label: "Clinic Requiring Energy" },
  business_request: { color: "#FB923C", label: "Business Requiring Energy" },
  minigrid_opportunity: { color: "#A78BFA", label: "Mini-Grid Opportunity" },
};

function SierraLeoneMap({ districts, locations, demand }) {
  const demandByDistrict = Object.fromEntries((demand || []).map((d) => [d.district, d.count]));
  const [selected, setSelected] = useState(null);
  return (
    <div className="rounded-lg border border-white/10 bg-pine/60 p-5 sm:p-7" data-testid="energy-access-map">
      <svg viewBox="0 0 100 100" className="w-full max-w-xl mx-auto" role="img" aria-label="Schematic map of Sierra Leone with energy access markers">
        <polygon
          points="18,14 34,6 52,8 68,14 80,26 84,40 78,54 70,66 58,78 44,86 30,84 18,72 10,58 8,42 12,26"
          fill="#0D261C" stroke="#1C4A34" strokeWidth="0.8"
        />
        {districts.map((d) => {
          const markers = locations.filter((l) => l.district === d.district);
          const demandCount = demandByDistrict[d.district] || 0;
          const active = markers.length > 0 || demandCount > 0;
          const color = markers.length ? TYPE_STYLES[markers[0].type]?.color || "#00E676" : demandCount ? "#38BDF8" : "#1C4A34";
          return (
            <g key={d.district} className="cursor-pointer" onClick={() => setSelected({ district: d.district, markers, demandCount })}
              data-testid={`map-district-${d.district.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <circle cx={d.x} cy={d.y} r={active ? 2.6 : 1.4} fill={color} opacity={active ? 1 : 0.6}>
                {active && <animate attributeName="r" values="2.6;3.4;2.6" dur="3s" repeatCount="indefinite" />}
              </circle>
              <circle cx={d.x} cy={d.y} r="5" fill="transparent" />
            </g>
          );
        })}
      </svg>
      {selected && (
        <div className="mt-4 rounded-md border border-white/10 bg-obsidian/60 p-4" data-testid="map-district-detail">
          <p className="font-display text-sm font-bold text-white">{selected.district}</p>
          {selected.markers.map((m) => (
            <p key={m.id} className="mt-1 text-xs text-slate-300">
              <span style={{ color: TYPE_STYLES[m.type]?.color }}>●</span> {m.name} — {TYPE_STYLES[m.type]?.label || m.type}{m.note ? ` · ${m.note}` : ""}
            </p>
          ))}
          {selected.demandCount > 0 && (
            <p className="mt-1 text-xs text-sky-300">● {selected.demandCount} communit{selected.demandCount > 1 ? "ies" : "y"} requesting energy</p>
          )}
          {!selected.markers.length && !selected.demandCount && (
            <p className="mt-1 text-xs text-slate-500">No published activity yet — request energy for your community below.</p>
          )}
        </div>
      )}
      <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
        Schematic district map — community requests shown at district level only. No personal data is published.
      </p>
    </div>
  );
}

export default function EnergyMap() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ community: "", district: "Bo", region: "", contact_person: "", phone: "", households: "", facilities: "", current_source: "", challenges: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/energy/map").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/energy/community", { ...form, households: form.households ? Number(form.households) : undefined });
      setDone(true);
      toast.success("Community request recorded");
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="BNM Energy Access Map — Sierra Leone" description="Communities, schools and clinics requesting energy across Sierra Leone. Request energy for your community." />
      <section data-testid="energy-map-page" className="bg-obsidian grid-lines-dark noise-overlay min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-32 pb-24">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-volt">BNM Energy Intelligence</p>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white text-balance">
            BNM Energy Access Map
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-400">
            Where energy is needed across Sierra Leone. BNM publishes locations as programmes are formalised — communities appear at district level only, and personal details are never published.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
            {!data ? <p className="text-sm text-slate-500">Loading map…</p> : (
              <SierraLeoneMap districts={data.districts} locations={data.locations} demand={data.community_demand} />
            )}

            <div className="rounded-lg border border-white/10 bg-pine/60 p-6 sm:p-8">
              <h2 className="font-display text-lg font-bold text-white">Request Energy for My Community</h2>
              {done ? (
                <div className="mt-5 rounded-md border border-volt/30 bg-volt/10 p-5 text-center" data-testid="community-success">
                  <p className="font-semibold text-volt">Thank you — your community has been recorded.</p>
                  <p className="mt-1.5 text-xs text-slate-400">BNM reviews every request. Yours now appears at district level on the map.</p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="community-form" className="mt-5 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <DarkField label="Community *" testid="community-name" value={form.community} onChange={set("community")} required />
                    <SelectInputDark label="District *" testid="community-district" value={form.district} onChange={set("district")}
                      options={(data?.districts || []).map((d) => d.district)} />
                    <DarkField label="Region" testid="community-region" value={form.region} onChange={set("region")} />
                    <DarkField label="Contact person *" testid="community-contact" value={form.contact_person} onChange={set("contact_person")} required />
                    <DarkField label="Phone / WhatsApp *" testid="community-phone" value={form.phone} onChange={set("phone")} required />
                    <label className="block">
                      <span className="block text-sm font-medium text-slate-300">Number of households</span>
                      <input type="number" min={1} value={form.households} data-testid="community-households"
                        onChange={(e) => set("households")(e.target.value)} className="mt-1.5 h-12 w-full rounded-md border border-white/15 bg-obsidian/60 px-3.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt" />
                    </label>
                  </div>
                  <DarkField label="Schools / clinics / businesses present" testid="community-facilities" value={form.facilities} onChange={set("facilities")} />
                  <DarkField label="Current electricity source" testid="community-source" value={form.current_source} onChange={set("current_source")} />
                  <label className="block">
                    <span className="block text-sm font-medium text-slate-300">Main energy challenges</span>
                    <textarea rows={3} value={form.challenges} data-testid="community-challenges" onChange={(e) => set("challenges")(e.target.value)}
                      className="mt-1.5 w-full rounded-md border border-white/15 bg-obsidian/60 px-3.5 py-2.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt" />
                  </label>
                  <input type="text" value={form.website} onChange={(e) => set("website")(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  {error && <div className="text-sm text-red-400">{error}</div>}
                  <button type="submit" disabled={busy} data-testid="community-submit"
                    className="w-full sm:w-auto rounded-md bg-volt px-7 py-3.5 font-semibold text-obsidian hover:bg-volt-soft hover:text-obsidian transition-colors disabled:opacity-60">
                    {busy ? "Sending…" : "Request Community Energy Assessment"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function DarkField({ label, value, onChange, testid, required }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-300">{label}</span>
      <input value={value} required={required} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 h-12 w-full rounded-md border border-white/15 bg-obsidian/60 px-3.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt" />
    </label>
  );
}

function SelectInputDark({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-300">{label}</span>
      <select value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 h-12 w-full rounded-md border border-white/15 bg-obsidian/60 px-3.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
